// src/lib/storage/BaseOperations.ts

import { syncEventEmitter } from "./sync-event-emitter";
import { LocalDatabase } from "./local-database.ts";
import { OperationType, QueuePayload, RemoteClient, SyncQueueItem, SyncStatus } from "./types";

/**
 * Base class for KV and ZSet operations
 * Handles common functionality like sync queue management and error handling
 */
export abstract class BaseOperations {
  private syncInProgress = false;
  private readonly maxRetries = 3;
  private readonly maxTimeout = 5000; // 5 seconds for remote operations
  private readonly operations: OperationType[];

  protected constructor(
    protected db: LocalDatabase,
    protected remoteClient: RemoteClient | null,
    operations: OperationType[],
  ) {
    this.operations = operations;
  }

  /**
   * Process pending items in the sync queue
   */
  public async processSyncQueue(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine || !this.remoteClient) {
      console.log("Sync skipped:", {
        syncInProgress: this.syncInProgress,
        isOnline: navigator.onLine,
        hasRemoteClient: !!this.remoteClient,
      });
      return;
    }

    try {
      this.syncInProgress = true;

      const pendingItems = await this.db.syncQueue
        .where("status")
        .equals("pending")
        .and((item) => this.operations.includes(item.operation))
        .toArray();

      console.log(`Processing ${pendingItems.length} pending items`);

      for (const item of pendingItems) {
        try {
          if (!item.id) {
            throw new Error("item id is null when processing queue items");
          }

          // Update status to processing
          await this.db.syncQueue.update(item.id, { status: "processing" });

          // Execute the remote operation
          await this.executeRemoteOperation(item);

          // Remove successful item
          await this.db.syncQueue.delete(item.id);

          // Emit success event
          syncEventEmitter.emit(item.key, true);

          console.log(`✅ Successfully synced operation:`, {
            operation: item.operation,
            key: item.key,
          });
        } catch (error) {
          console.error("Sync error:", error);

          // Emit failure event
          syncEventEmitter.emit(item.key, false);

          await this.handleSyncError(item);
        }
      }
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Add an operation to the sync queue
   */
  protected async queueOperation<P extends QueuePayload>(
    operation: OperationType,
    key: string,
    payload: P,
  ): Promise<void> {
    await this.db.syncQueue.add({
      operation,
      key,
      payload,
      timestamp: Date.now(),
      status: "pending",
      retryCount: 0,
    });
  }

  /**
   * Execute a remote operation with timeout
   */
  protected async fetchWithTimeout<T>(
    fn: () => Promise<T>,
    timeout: number = this.maxTimeout,
  ): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Operation timed out")), timeout);
    });

    return Promise.race([fn(), timeoutPromise]);
  }

  /**
   * Handle errors during sync operations
   */
  protected async handleSyncError(item: SyncQueueItem): Promise<void> {
    console.error(`Failed to process operation:`, {
      operation: item.operation,
      key: item.key,
      retryCount: item.retryCount,
    });

    const newRetryCount = (item.retryCount || 0) + 1;
    const newStatus: SyncStatus =
      newRetryCount >= this.maxRetries ? "failed" : "pending";

    await this.db.syncQueue.update(item.id!, {
      retryCount: newRetryCount,
      status: newStatus,
    });

    if (newStatus === "failed") {
      console.error(
        `Operation failed permanently after ${this.maxRetries} retries:`,
        {
          operation: item.operation,
          key: item.key,
        },
      );
    }
  }

  /**
   * Abstract method to be implemented by derived classes
   * Executes the remote operation based on the sync queue item
   */
  protected abstract executeRemoteOperation(item: SyncQueueItem): Promise<void>;

  /**
   * Get the sync status
   */
  protected async getSyncStatus(): Promise<{
    syncInProgress: boolean;
    pendingCount: number;
    failedCount: number;
  }> {
    const [pendingCount, failedCount] = await Promise.all([
      this.db.syncQueue.where("status").equals("pending").count(),
      this.db.syncQueue.where("status").equals("failed").count(),
    ]);

    return {
      syncInProgress: this.syncInProgress,
      pendingCount,
      failedCount,
    };
  }

  /**
   * Clear failed sync operations
   */
  protected async clearFailedOperations(): Promise<number> {
    return this.db.syncQueue.where("status").equals("failed").delete();
  }

  /**
   * Retry failed operations
   */
  protected async retryFailedOperations(): Promise<void> {
    const failedItems = await this.db.syncQueue
      .where("status")
      .equals("failed")
      .toArray();

    for (const item of failedItems) {
      await this.db.syncQueue.update(item.id!, {
        status: "pending",
        retryCount: 0,
      });
    }

    if (failedItems.length > 0) {
      await this.processSyncQueue();
    }
  }
}
